import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import IntroSection from '../../components/Profile/intro-section/intro-section.component.jsx';
import { GeneralSettings } from './components/GeneralSettings';
import { PasswordSettings } from './components/PasswordSettings';
import NotificationSettings from '../../components/Header/Notification/notification-settings.component.jsx';
import { Box, Text } from '@chakra-ui/react';
import IgnoredContent from './components/IgnoredContent';
import SEO from '../../components/SEO';
import { generateSEOMetadata, dynamicPages } from '../../utils/seo';

const listItemMenu = [
  {
    name: 'General',
    slug: 'general'
  },
  {
    name: 'Password',
    slug: 'password'
  },
  {
    name: 'Notifications',
    slug: 'notifications'
  },
  {
    name: 'Ignored Content',
    slug: 'ignored-content'
  }
];

const AccountSettings: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [profileData, setProfileData] = useState(null);
  const [type, setType] = useState<string>('general');

  const handleChangeProfile = (newProfile) => {
    setProfileData(newProfile);
    localStorage.setItem('user', JSON.stringify(newProfile));
  };

  const handleSelectedTab = async (value: string) => {
    setType(value);
    setSearchParams({ tab: value });
  };

  useEffect(() => {
    const raw = localStorage.getItem('user');
    if (!raw || raw === 'undefined' || raw === 'null') return;

    try {
      const user = JSON.parse(raw);
      setProfileData(user);
    } catch (err) {
      console.error('Invalid user JSON:', err);
    }
  }, []);

  // Sync selected tab with URL query param on mount and when it changes
  useEffect(() => {
    const tabParam = searchParams.get('tab');

    // If URL has a valid tab, use it
    if (tabParam && listItemMenu.some((item) => item.slug === tabParam)) {
      setType(tabParam);
      return;
    }

    // Otherwise default to "general" and ensure URL reflects it
    setType('general');
    setSearchParams({ tab: 'general' });
  }, [searchParams, setSearchParams]);

  // Generate SEO metadata for account settings page
  const seoMetadata = generateSEOMetadata.dynamicPage(
    dynamicPages['account-settings'].name,
    dynamicPages['account-settings'].url,
    dynamicPages['account-settings'].description
  );

  return (
    <div className="bg-[#FAFAFA] h-[calc(100vh-68px] md:h-[calc(100vh-80px)]">
      <SEO {...seoMetadata} />

      <div className="py-[30px] w-full 2xl:py-8 h-full max-w-[1308px] mx-auto px-4 md:px-[40px]">
        <IntroSection title="Account Settings" description="Update your account settings" />
        <div className="md:grid grid-cols-[296px_auto] mt-6 md:mt-[38px]">
          <div className="pr-0 md:pr-[64px] md:border-r border-normal-gray flex flex-row md:flex-col gap-1 overflow-x-auto no-scrollbar">
            {listItemMenu.map((item, index) => (
              <Box
                key={index}
                cursor="pointer"
                display="flex"
                alignItems="center"
                justifyContent={{ base: 'center', md: 'space-between' }}
                py="8px"
                minW={{ base: '167px', md: '232px' }}
                pl={{ base: '0px', md: '30px' }}
                bg={type === item.slug ? '#C8E6C9' : 'transparent'}
                _hover={{ bg: type === item.slug ? '#C8E6C9' : '#F0F7F2', borderRadius: '8px' }}
                borderRadius="8px"
                onClick={() => handleSelectedTab(item.slug)}
              >
                <Text
                  fontWeight="800"
                  color="#204A22"
                  fontSize="lg"
                  transition="all 0.15s ease-out"
                  whiteSpace="nowrap"
                >
                  {item.name}
                </Text>
              </Box>
            ))}
          </div>
          <div className="md:mt-0 mt-[24px] md:px-4 max-h-unset md:max-h-[70vh] overflow-auto">
            {type === 'general' && (
              <GeneralSettings profileData={profileData} setProfileData={handleChangeProfile} />
            )}
            {type === 'password' && <PasswordSettings profileData={profileData} />}
            {type === 'notifications' && <NotificationSettings />}
            {type === 'ignored-content' && <IgnoredContent />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSettings;
